import React, { useState } from 'react';
import { 
  Shield, Search, Globe, TrendingUp, TrendingDown, AlertTriangle,
  Eye, MessageSquare, Share2, Users, BarChart3, PieChart,
  MapPin, Calendar, Clock, Target, Zap, Filter, RefreshCw,
  ExternalLink, Download, Bell, Star, ThumbsUp, ThumbsDown,
  Hash, AtSign, Link, Newspaper, Youtube, Instagram, Twitter
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import FloatingParticles from './FloatingParticles';
import EnhancedCard from './EnhancedCard';
import EnhancedButton from './EnhancedButton';
import PageHeader from './ui/PageHeader';
import '../styles/neutral-theme.css';
import '../styles/osint-modern.css';

const InteractiveOSINT = () => {
  const { t } = useLanguage();
  const [activeTimeframe, setActiveTimeframe] = useState('7days');
  const [selectedSource, setSelectedSource] = useState('all');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showDetailsFor, setShowDetailsFor] = useState(null);

  // OSINT Data Sources
  const dataSources = [
    { id: 'social', name: t('osint.sources.social'), icon: Share2, count: 2847, status: 'active' },
    { id: 'news', name: t('osint.sources.news'), icon: Newspaper, count: 156, status: 'active' },
    { id: 'forums', name: t('osint.sources.forums'), icon: MessageSquare, count: 93, status: 'active' },
    { id: 'review', name: t('osint.sources.review'), icon: Star, count: 284, status: 'active' },
    { id: 'video', name: t('osint.sources.video'), icon: Youtube, count: 47, status: 'active' }
  ];

  // Real-time Metrics
  const realtimeMetrics = {
    totalMentions: 3427,
    sentimentScore: 72,
    reachEstimate: '1.2M',
    engagementRate: 8.4,
    criticalAlerts: 3,
    trendingTopics: 12
  };

  // Sentiment Analysis
  const sentimentData = [
    { type: t('osint.sentimentTypes.positive'), count: 2156, percentage: 63, color: '#22c55e', trend: '+12%' },
    { type: t('osint.sentimentTypes.neutral'), count: 984, percentage: 29, color: '#64748b', trend: '+3%' },
    { type: t('osint.sentimentTypes.negative'), count: 287, percentage: 8, color: '#ef4444', trend: '-5%' }
  ];

  // Geographic Distribution
  const geoData = [
    { country: t('osint.countries.spain'), mentions: 1847, sentiment: 68, flag: '🇪🇸' },
    { country: t('osint.countries.mexico'), mentions: 432, sentiment: 74, flag: '🇲🇽' },
    { country: t('osint.countries.argentina'), mentions: 298, sentiment: 61, flag: '🇦🇷' },
    { country: t('osint.countries.colombia'), mentions: 187, sentiment: 79, flag: '🇨🇴' },
    { country: t('osint.countries.chile'), mentions: 156, sentiment: 83, flag: '🇨🇱' }
  ];

  // Trending Topics and Keywords
  const trendingTopics = [
    { 
      keyword: '#CalidadServicio', 
      mentions: 847, 
      trend: 'up', 
      sentiment: 78, 
      change: '+156%',
      category: 'service' 
    },
    { 
      keyword: 'Atención Cliente', 
      mentions: 623, 
      trend: 'up', 
      sentiment: 65, 
      change: '+89%',
      category: 'support' 
    },
    { 
      keyword: '#Facturación', 
      mentions: 445, 
      trend: 'down', 
      sentiment: 34, 
      change: '-23%',
      category: 'billing' 
    },
    { 
      keyword: 'Nuevo Producto', 
      mentions: 387, 
      trend: 'up', 
      sentiment: 91, 
      change: '+245%',
      category: 'product' 
    }
  ];

  // Critical Alerts
  const criticalAlerts = [
    {
      id: 1,
      type: 'negative_spike',
      severity: 'high',
      message: t('osint.alertTypes.negative_spike'),
      source: 'Twitter',
      mentions: 47,
      timestamp: '15 min ago',
      sentiment: 12
    },
    {
      id: 2,
      type: 'competitor_mention',
      severity: 'medium',
      message: t('osint.alertTypes.competitor_mention'),
      source: 'Reddit',
      mentions: 23,
      timestamp: '32 min ago',
      sentiment: 67
    },
    {
      id: 3,
      type: 'viral_content',
      severity: 'low',
      message: t('osint.alertTypes.viral_content'),
      source: 'Instagram',
      mentions: 156,
      timestamp: '1 hour ago',
      sentiment: 94
    }
  ];

  // Intelligence Reports
  const intelligenceReports = [
    {
      title: 'Análisis Competitivo Semanal',
      type: 'competitive',
      insights: 8,
      confidence: 87,
      lastUpdate: '2h ago',
      summary: 'Incremento del 34% en menciones vs competidores principales'
    },
    {
      title: 'Detección de Crisis Reputacional',
      type: 'reputation',
      insights: 12,
      confidence: 94,
      lastUpdate: '4h ago',
      summary: 'Riesgo bajo detectado en sector facturación'
    },
    {
      title: 'Oportunidades de Engagement',
      type: 'opportunity',
      insights: 15,
      confidence: 78,
      lastUpdate: '6h ago',
      summary: 'Identificadas 15 oportunidades de interacción positiva'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#22c55e';
      default: return '#64748b';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'service': return '#3b82f6';
      case 'support': return '#8b5cf6';
      case 'billing': return '#ef4444';
      case 'product': return '#22c55e';
      default: return '#64748b';
    }
  };

  return (
    <div className="interactive-osint dashboard-container">
      <FloatingParticles count={30} />
      
      <PageHeader
        title={t('osint.intelligenceCenter')}
        subtitle={t('osint.intelligenceDesc')}
        icon={<Shield />}
        badge={
          <div className="osint-status-badge">
            <div className="pulse-dot"></div>
            <span>{t('osint.realtime')}</span>
          </div>
        }
        actions={
          <>
            <div className="timeframe-selector">
              {['24h', '7days', '30days', '90days'].map(period => (
                <button
                  key={period}
                  className={`timeframe-btn ${activeTimeframe === period ? 'active' : ''}`}
                  onClick={() => setActiveTimeframe(period)}
                >
                  {t(`osint.timeframes.${period}`)}
                </button>
              ))}
            </div>
            <EnhancedButton size="small" variant="secondary" icon={<RefreshCw size={16} />}>
              {t('osint.refresh')}
            </EnhancedButton>
            <EnhancedButton size="small" variant="secondary" icon={<Download size={16} />}>
              {t('osint.export')}
            </EnhancedButton>
          </>
        }
      />

      {/* Real-time Metrics Dashboard */}
      <div className="metrics-grid">
        <div className="metric-card primary">
          <div className="metric-header">
            <MessageSquare size={24} />
            <span className="metric-change positive">+12%</span>
          </div>
          <div className="metric-value">{realtimeMetrics.totalMentions.toLocaleString()}</div>
          <div className="metric-label">{t('osint.metrics.totalMentions')}</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <TrendingUp size={24} />
            <span className="metric-change positive">+5pts</span>
          </div>
          <div className="metric-value">{realtimeMetrics.sentimentScore}%</div>
          <div className="metric-label">{t('osint.metrics.sentimentScore')}</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Users size={24} />
            <span className="metric-change positive">+23%</span>
          </div>
          <div className="metric-value">{realtimeMetrics.reachEstimate}</div>
          <div className="metric-label">{t('osint.metrics.reachEstimate')}</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Target size={24} />
            <span className="metric-change positive">+1.2%</span>
          </div>
          <div className="metric-value">{realtimeMetrics.engagementRate}%</div>
          <div className="metric-label">{t('osint.metrics.engagementRate')}</div>
        </div>

        <div className="metric-card alert">
          <div className="metric-header">
            <AlertTriangle size={24} />
            <span className="metric-change negative">+1</span>
          </div>
          <div className="metric-value">{realtimeMetrics.criticalAlerts}</div>
          <div className="metric-label">{t('osint.metrics.criticalAlerts')}</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Hash size={24} />
            <span className="metric-change positive">+3</span>
          </div>
          <div className="metric-value">{realtimeMetrics.trendingTopics}</div>
          <div className="metric-label">{t('osint.metrics.trendingTopics')}</div>
        </div>
      </div>

      {/* Main Intelligence Dashboard */}
      <div className="intelligence-layout">
        
        {/* Left Column - Sources and Sentiment */}
        <div className="left-column">
          
          {/* Data Sources Monitoring */}
          <div className="widget">
            <div className="widget-header">
              <h3>Fuentes de Datos</h3>
              <div className="sources-legend">
                <span className="legend-item active">● Activo</span>
                <span className="legend-item">○ Inactivo</span>
              </div>
            </div>
            <div className="sources-list">
              {dataSources.map(source => {
                const IconComponent = source.icon;
                return (
                  <div key={source.id} className="source-item">
                    <div className="source-info">
                      <IconComponent size={20} />
                      <div>
                        <div className="source-name">{source.name}</div>
                        <div className="source-count">{source.count.toLocaleString()} menciones</div>
                      </div>
                    </div>
                    <div className={`source-status ${source.status}`}></div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sentiment Analysis */}
          <div className="widget">
            <div className="widget-header">
              <h3>Análisis de Sentimiento</h3>
              <div className="sentiment-score">
                Score: <strong>{realtimeMetrics.sentimentScore}%</strong>
              </div>
            </div>
            <div className="sentiment-breakdown">
              {sentimentData.map(sentiment => (
                <div key={sentiment.type} className="sentiment-item">
                  <div className="sentiment-bar">
                    <div 
                      className="sentiment-fill"
                      style={{ 
                        width: `${sentiment.percentage}%`, 
                        backgroundColor: sentiment.color 
                      }}
                    ></div>
                  </div>
                  <div className="sentiment-details">
                    <div className="sentiment-label">
                      {sentiment.type}
                      <span className="sentiment-trend" style={{ color: sentiment.color }}>
                        {sentiment.trend}
                      </span>
                    </div>
                    <div className="sentiment-stats">
                      {sentiment.count.toLocaleString()} • {sentiment.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Center Column - Trending Topics and Geographic */}
        <div className="center-column">
          
          {/* Trending Topics */}
          <div className="widget">
            <div className="widget-header">
              <h3>Trending Topics</h3>
              <div className="trending-filters">
                <button className="filter-btn active">Todo</button>
                <button className="filter-btn">Positivo</button>
                <button className="filter-btn">Negativo</button>
              </div>
            </div>
            <div className="trending-list">
              {trendingTopics.map((topic, index) => (
                <div key={index} className="trending-item">
                  <div className="trending-info">
                    <div className="trending-keyword">
                      <Hash size={14} />
                      {topic.keyword}
                    </div>
                    <div className="trending-stats">
                      {topic.mentions.toLocaleString()} menciones
                    </div>
                  </div>
                  <div className="trending-metrics">
                    <div className={`trending-change ${topic.trend}`}>
                      {topic.trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {topic.change}
                    </div>
                    <div 
                      className="sentiment-badge"
                      style={{ backgroundColor: getCategoryColor(topic.category) }}
                    >
                      {topic.sentiment}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="widget">
            <div className="widget-header">
              <h3>Distribución Geográfica</h3>
              <MapPin size={16} />
            </div>
            <div className="geo-list">
              {geoData.map((country, index) => (
                <div key={index} className="geo-item">
                  <div className="geo-info">
                    <span className="country-flag">{country.flag}</span>
                    <div>
                      <div className="country-name">{country.country}</div>
                      <div className="country-mentions">{country.mentions.toLocaleString()} menciones</div>
                    </div>
                  </div>
                  <div className="geo-sentiment">
                    <div className="sentiment-bar-mini">
                      <div 
                        className="sentiment-fill-mini"
                        style={{ width: `${country.sentiment}%` }}
                      ></div>
                    </div>
                    <span className="sentiment-value">{country.sentiment}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Right Column - Alerts and Intelligence */}
        <div className="right-column">
          
          {/* Critical Alerts */}
          <div className="widget">
            <div className="widget-header">
              <h3>Alertas Críticas</h3>
              <Bell size={16} />
            </div>
            <div className="alerts-list">
              {criticalAlerts.map(alert => (
                <div key={alert.id} className="alert-item">
                  <div 
                    className="alert-severity"
                    style={{ backgroundColor: getSeverityColor(alert.severity) }}
                  ></div>
                  <div className="alert-content">
                    <div className="alert-message">{alert.message}</div>
                    <div className="alert-details">
                      <span className="alert-source">{alert.source}</span>
                      <span className="alert-mentions">{alert.mentions} menciones</span>
                      <span className="alert-time">{alert.timestamp}</span>
                    </div>
                  </div>
                  <div className="alert-sentiment">
                    {alert.sentiment}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Intelligence Reports */}
          <div className="widget">
            <div className="widget-header">
              <h3>Informes de Inteligencia</h3>
              <BarChart3 size={16} />
            </div>
            <div className="intelligence-list">
              {intelligenceReports.map((report, index) => (
                <div key={index} className="intelligence-item">
                  <div className="intelligence-header">
                    <div className="report-title">{report.title}</div>
                    <div className="report-time">{report.lastUpdate}</div>
                  </div>
                  <div className="intelligence-metrics">
                    <div className="metric">
                      <Eye size={14} />
                      {report.insights} insights
                    </div>
                    <div className="confidence">
                      <Target size={14} />
                      {report.confidence}% confianza
                    </div>
                  </div>
                  <div className="report-summary">{report.summary}</div>
                  <button className="view-report-btn">
                    <ExternalLink size={12} />
                    Ver Completo
                  </button>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Estilos movidos a osint-modern.css */}
    </div>
  );
};

export default InteractiveOSINT;